import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, mapRoleCodeToLegacyId } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { LogIn, AlertCircle, ShieldCheck, ArrowRight, KeyRound, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { getPrimaryRoleCode } from '@/lib/authBootstrap';

export default function Login({ returnTo = '/foundation', nfcMode = false }) {
  const { signIn } = useAuth();
  const { loginConRuolo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showForgot, setShowForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { me } = await signIn(email.trim(), password);
      const primary = getPrimaryRoleCode(me);
      const legacyId = mapRoleCodeToLegacyId(primary);
      if (legacyId) loginConRuolo(legacyId);
      navigate(returnTo, { replace: nfcMode });
    } catch (e) {
      setErr(e.message || 'Credenziali non valide');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-safe-area bg-app-background flex items-center justify-center px-5" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-7 sm:p-9">
          {/* Logo ufficiale */}
          <div className="flex justify-center mb-6">
            <img
              src="/brand/restaurantos-logo-horizontal.svg"
              alt="RestaurantOS · Gestisci · Organizza · Cresci"
              className="h-12 sm:h-14 w-auto"
              data-testid="brand-logo-login"
            />
          </div>

          {/* Accesso protetto */}
          <div className="flex items-center gap-2 text-brand-green text-sm font-semibold mb-5">
            <ShieldCheck className="w-4 h-4" />
            Accesso protetto
          </div>

          {/* Titolo */}
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-brand-navy leading-none">
            {nfcMode ? 'Accedi per registrare la presenza' : 'Bentornato'}
          </h1>
          <p className="mt-4 text-stone-500 text-base leading-relaxed">
            {nfcMode
              ? 'Dopo l’accesso completeremo la richiesta autorizzata.'
              : 'Accedi con il tuo account. Non servono chiavi segrete o procedure tecniche.'}
          </p>
          {location.state?.passwordUpdated && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              role="status" data-testid="password-updated">
              Password aggiornata. Accedi con la nuova password.
            </div>
          )}

          {/* Form */}
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-stone-700 text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="nome@azienda.it"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-stone-700 text-sm font-medium">Password</Label>
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  data-testid="link-forgot-password"
                  className="text-xs text-brand-blue hover:underline font-medium"
                >
                  Password dimenticata?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder="La tua password"
                className="mt-1.5 h-11"
              />
            </div>
            {err && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-12 text-base font-semibold bg-brand-green hover:bg-brand-green-hover active:bg-brand-green-active text-white gap-2 shadow-sm"
            >
              {busy ? (
                <>
                  <LogIn className="w-4 h-4" />
                  Accesso in corso…
                </>
              ) : (
                <>
                  Accedi <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal defaultEmail={email} onClose={() => setShowForgot(false)} />}
    </div>
  );
}

function ForgotPasswordModal({ defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await apiFetch('/api/auth/password-reset', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (_) {
      // Neutral: never reveal enumeration
      setSent(true);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="forgot-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Password dimenticata</div>
          <button onClick={onClose} data-testid="forgot-close"><X className="w-5 h-5 text-stone-400 hover:text-brand-navy" /></button>
        </div>
        {sent ? (
          <div className="space-y-3" data-testid="forgot-sent">
            <p className="text-sm text-brand-navy">Se l'email è collegata a un account RestaurantOS, riceverai a breve un messaggio con le istruzioni per creare una nuova password.</p>
            <Button onClick={onClose} className="w-full bg-brand-green hover:bg-brand-green-hover text-white">Ho capito</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-stone-600">Inserisci la tua email di accesso. Riceverai un link sicuro per creare una nuova password.</p>
            <div>
              <Label htmlFor="fpw">Email</Label>
              <Input id="fpw" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1.5 h-11" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-brand-green hover:bg-brand-green-hover text-white gap-2" data-testid="forgot-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Invio…</> : <><KeyRound className="w-4 h-4" /> Invia link di reset</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
